/**
 * 成本管控（Alice 页面 costControls 的复刻）：渠道走账标志、仅记账账号的模型改写、每日上限规则、告警阈值。
 *
 * 背景：Alice 把内部员工账号的请求导到「内部壳」模型、不走账的 Provider 上，成本记 0。本仓没有「内部账号」
 *   标记，对应概念是「仅记账」请求（billing_mode = metered）；渠道标志、改写目标、上限规则与阈值都已存库并审计。
 * 设计意图：保持 Alice 的四个区块与交互；每次保存立即重新加载，页面只展示后端返回的最新状态。
 *   support 角色只读（控件禁用），写操作的权限由后端判断。阈值金额是美元（Alice 是人民币）。
 * 关键约束：改写与每日上限目前只保存，数据面还没有按它们执行，页面用提示条写明，避免被当成已生效的管控。
 */
import { useEffect, useMemo, useState } from 'react'
import { bizApi, type CapRule, type CostControls as Data } from '@ap/api-client/admin-biz'
import { A } from '@ap/i18n/admin'
import { BIZ } from '@ap/i18n/admin-biz'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Link, Notice, useLoad, useToast } from '@ap/ui'
import { NUM_FIELD, bizError } from '../../alice/biz-kit.tsx'
import { OpsNote, OpsPage } from '../../alice/kit.tsx'
import { useAdmin } from '../../session.tsx'
import { useUsd } from '../../shared.tsx'

/** 区块卡片内的工具条：去掉 .ops-toolbar 自带的外框与阴影，避免卡片里再套一层卡片。 */
const FLAT_BAR = { border: 0, borderRadius: 0, boxShadow: 'none', margin: 0, padding: '12px 16px' } as const

const TH_KEYS = ['account_daily_usd', 'total_daily_usd', 'account_hourly_usd'] as const
type ThKey = (typeof TH_KEYS)[number]

export function CostControls() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const usd = useUsd()
  const { isAdmin } = useAdmin()
  const load = useLoad(() => bizApi.costControls(), [])
  const d: Data | null = load.data

  const [filter, setFilter] = useState('')
  const [redirects, setRedirects] = useState<Record<string, string>>({})
  const [caps, setCaps] = useState<{ name: string; match: string; cap_usd: string }[]>([])
  const [th, setTh] = useState<Record<ThKey, string>>({ account_daily_usd: '', total_daily_usd: '', account_hourly_usd: '' })
  const [busy, setBusy] = useState(false)

  // 服务端状态变化后，把可编辑的副本重置成最新值
  useEffect(() => {
    if (!d) return
    setRedirects(Object.fromEntries(d.models.map((m) => [m.id, m.internal_redirect_to])))
    setCaps(d.cap_rules.map((r) => ({ name: r.name, match: r.match, cap_usd: String(r.cap_usd) })))
    setTh({
      account_daily_usd: String(d.thresholds.account_daily_usd),
      total_daily_usd: String(d.thresholds.total_daily_usd),
      account_hourly_usd: String(d.thresholds.account_hourly_usd),
    })
  }, [d])
  useEffect(() => {
    if (load.error) toast.error(bizError(load.error, locale))
  }, [load.error, toast, locale])

  const shownModels = useMemo(() => {
    if (!d) return []
    const q = filter.trim().toLowerCase()
    return q ? d.models.filter((m) => `${m.id} ${m.display_name}`.toLowerCase().includes(q)).slice(0, 60) : d.models.filter((m) => m.internal_redirect_to)
  }, [d, filter])

  /** 保存动作的共同外壳：提交中禁用按钮，成功提示并重载，失败显示本地化错误并重载回服务端状态。 */
  const run = async (fn: () => Promise<unknown>, okText: string) => {
    setBusy(true)
    try {
      await fn()
      toast.ok(okText)
    } catch (e) {
      console.error('[cost-controls] 保存失败', e)
      toast.error(bizError(e, locale))
    } finally {
      setBusy(false)
      load.reload()
    }
  }

  const saveCaps = () => {
    const rules: CapRule[] = caps.map((r) => ({ name: r.name.trim(), match: r.match.trim(), cap_usd: Number(r.cap_usd) }))
    return run(() => bizApi.putCapRules(rules), t(BIZ.cc.capsSaved))
  }
  const saveThresholds = () =>
    run(
      () =>
        bizApi.putThresholds({
          account_daily_usd: Number(th.account_daily_usd),
          total_daily_usd: Number(th.total_daily_usd),
          account_hourly_usd: Number(th.account_hourly_usd),
        }),
      t(BIZ.cc.thSaved),
    )

  return (
    <OpsPage eyebrow="COST CONTROLS" title={t(A.nav.costControls)} desc={t(BIZ.cc.desc)} chip={t(BIZ.cc.chip)}>
      <OpsNote>{t(BIZ.cc.note)}</OpsNote>
      <Notice tone="accent">{t(BIZ.cc.notEnforced)}</Notice>
      {!isAdmin && <Notice tone="accent">{t(A.role.readOnly)}</Notice>}

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.cc.provTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.cc.provCopy)}</div>
          </div>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(BIZ.cc.colProvider)}</th>
                  <th>{t(C.status)}</th>
                  <th>{t(BIZ.cc.colAccounting)}</th>
                </tr>
              </thead>
              <tbody>
                {!d ? (
                  <tr>
                    <td colSpan={3} style={{ color: 'var(--text-tertiary)' }}>
                      {t(BIZ.common.loading)}
                    </td>
                  </tr>
                ) : d.providers.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ color: 'var(--text-tertiary)' }}>
                      {t(BIZ.cc.noProviders)}
                    </td>
                  </tr>
                ) : (
                  d.providers.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.name || p.id} <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{p.id}</span>
                      </td>
                      <td>{t(p.is_active ? BIZ.cc.enabled : BIZ.cc.disabled)}</td>
                      <td>
                        <select
                          style={NUM_FIELD}
                          value={p.cost_accounting}
                          disabled={!isAdmin || busy}
                          onChange={(e) => run(() => bizApi.setProviderAccounting(p.id, e.target.value as 'billed' | 'unbilled'), t(BIZ.cc.saved))}
                        >
                          <option value="billed">{t(BIZ.cc.billed)}</option>
                          <option value="unbilled">{t(BIZ.cc.unbilled)}</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="ops-section-card" style={{ marginTop: 16 }}>
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.cc.redirTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.cc.redirCopy)}</div>
          </div>
        </div>
        <div className="ops-toolbar" style={FLAT_BAR}>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t(BIZ.cc.redirSearch)}
            style={{ ...NUM_FIELD, padding: '7px 10px', minWidth: 280 }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(BIZ.cc.redirHint)}</span>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(BIZ.cc.colRedirFrom)}</th>
                  <th>{t(C.status)}</th>
                  <th>{t(BIZ.cc.colRedirTo)}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shownModels.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ color: 'var(--text-tertiary)' }}>
                      {t(BIZ.cc.noModels)}
                    </td>
                  </tr>
                ) : (
                  shownModels.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <span className="mono">{m.id}</span>
                        {m.display_name && <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{m.display_name}</div>}
                      </td>
                      <td>{t(m.is_active ? BIZ.cc.enabled : BIZ.cc.disabled)}</td>
                      <td>
                        <input
                          value={redirects[m.id] ?? ''}
                          disabled={!isAdmin || busy}
                          onChange={(e) => setRedirects((r) => ({ ...r, [m.id]: e.target.value }))}
                          placeholder={t(BIZ.cc.redirPlaceholder)}
                          style={{ ...NUM_FIELD, width: '100%', minWidth: 260 }}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!isAdmin || busy}
                          onClick={() => {
                            const v = (redirects[m.id] ?? '').trim()
                            return run(() => bizApi.setRedirect(m.id, v), t(v ? BIZ.cc.redirSaved : BIZ.cc.redirCleared))
                          }}
                        >
                          {t(BIZ.common.save)}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="ops-section-card" style={{ marginTop: 16 }}>
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.cc.capTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.cc.capCopy)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px 0' }}>
          {caps.length === 0 && <div style={{ color: 'var(--text-tertiary)', fontSize: 12, padding: '0 0 4px' }}>{t(BIZ.cc.capEmpty)}</div>}
          {caps.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                placeholder={t(BIZ.cc.capName)}
                value={r.name}
                disabled={!isAdmin || busy}
                onChange={(e) => setCaps((c) => c.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                style={{ ...NUM_FIELD, width: 140 }}
              />
              <input
                placeholder={t(BIZ.cc.capMatch)}
                value={r.match}
                disabled={!isAdmin || busy}
                onChange={(e) => setCaps((c) => c.map((x, j) => (j === i ? { ...x, match: e.target.value } : x)))}
                style={{ ...NUM_FIELD, width: 160 }}
              />
              <input
                type="number"
                min={0}
                step={0.5}
                placeholder={t(BIZ.cc.capUsd)}
                value={r.cap_usd}
                disabled={!isAdmin || busy}
                onChange={(e) => setCaps((c) => c.map((x, j) => (j === i ? { ...x, cap_usd: e.target.value } : x)))}
                style={{ ...NUM_FIELD, width: 120 }}
              />
              <button className="btn btn-secondary btn-sm" disabled={!isAdmin || busy} onClick={() => setCaps((c) => c.filter((_, j) => j !== i))}>
                {t(BIZ.common.delete)}
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px 14px' }}>
          <button className="btn btn-secondary btn-sm" disabled={!isAdmin || busy} onClick={() => setCaps((c) => [...c, { name: '', match: '', cap_usd: '' }])}>
            {t(BIZ.cc.capAdd)}
          </button>
          <button className="btn btn-primary btn-sm" disabled={!isAdmin || busy} onClick={saveCaps}>
            {t(BIZ.cc.capSave)}
          </button>
        </div>
      </div>

      <div className="ops-section-card" style={{ marginTop: 16 }}>
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.cc.thTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.cc.thCopy)}</div>
          </div>
        </div>
        <div className="ops-toolbar" style={FLAT_BAR}>
          {TH_KEYS.map((k) => (
            <label key={k} style={{ fontSize: 12 }}>
              {t(BIZ.cc[k])}{' '}
              <input
                type="number"
                min={0}
                value={th[k]}
                disabled={!isAdmin || busy}
                onChange={(e) => setTh((x) => ({ ...x, [k]: e.target.value }))}
                style={{ ...NUM_FIELD, width: 90 }}
              />
            </label>
          ))}
          <button className="btn btn-primary btn-sm" disabled={!isAdmin || busy} onClick={saveThresholds}>
            {t(BIZ.cc.thSave)}
          </button>
        </div>
      </div>

      <div className="ops-section-card" style={{ marginTop: 16 }}>
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.cc.watchTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.cc.watchCopy)}</div>
          </div>
          <small style={{ color: 'var(--text-tertiary)' }}>{d?.watch.date}</small>
        </div>
        {d && (
          <>
            <div style={{ fontSize: 13, padding: '12px 16px 8px', color: d.watch.total_over ? 'var(--error-text)' : 'var(--text-secondary)' }}>
              {t(BIZ.cc.watchTotal, {
                c: usd(d.watch.total_cost_units),
                l: d.watch.total_limit_units > 0 ? usd(d.watch.total_limit_units) : t(BIZ.cc.watchOff),
              })}
            </div>
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <div className="table-inner">
                <table>
                  <thead>
                    <tr>
                      <th>{t(BIZ.cc.colOrg)}</th>
                      <th>{t(BIZ.cc.colWindow)}</th>
                      <th>{t(BIZ.cc.colCost)}</th>
                      <th>{t(BIZ.cc.colLimit)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.watch.accounts.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ color: 'var(--text-tertiary)' }}>
                          {t(BIZ.cc.watchNone)}
                        </td>
                      </tr>
                    ) : (
                      d.watch.accounts.map((o) => (
                        <tr key={`${o.account_id}-${o.window}`}>
                          <td>
                            <Link href={`/accounts/${o.account_id}`}>{o.name}</Link>
                          </td>
                          <td>{t(o.window === 'daily' ? BIZ.cc.winDaily : BIZ.cc.winHourly)}</td>
                          <td style={{ color: 'var(--error-text)', fontWeight: 650 }}>{usd(o.cost_units)}</td>
                          <td>{usd(o.limit_units)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </OpsPage>
  )
}
